<script>
import { mapState, mapActions } from 'vuex';
import router from '../../../router';
import store from '../../../store';
import TwImageUpload from '../../../components/TwImageUpload';
import SkiAreaSlopeMapsImageList from '../../../components/ski/areas/SkiAreaSlopeMapsImageList';
import i18n from '../../../i18n';
import TwCopyAndTranslateModal from '../../../components/TwCopyAndTranslateModal.vue';

export default {
  components: {
    TwCopyAndTranslateModal,
    TwImageUpload,
    SkiAreaSlopeMapsImageList,
  },
  data() {
    return {
      snackbar: false,
      deleteSuccess: false,
      saveSuccess: false,
      rearrangeSuccess: false,
      updateSuccess: false,
      error: false,
      copyAndTranslateModalOpen: false,
    };
  },
  computed: {
    ...mapState({
      slopeMaps: state => state.areas.slopeMaps.items,
      area: state => state.areas.area,
      slopeMapError: state => state.areas.slopeMaps.error,
      loading: state => state.areas.slopeMaps.loading,
      domains: state => state.twenty.domains.items,
    }),
    getCopyValues() {
      const values = [];

      this.slopeMaps.forEach(map => {
        values.push(
          {
            globalModelId: map.id,
            globalModelColumnName: 'ski_slope_map_id',
            selectKeyOverrides: [ 'domain_id', 'ski_slope_map_id' ],
            tableName: 'domain_ski_slope_map',
            sourceDomainId: null,
            selectedFromDomain: null,
            fromLanguageIsoOverride: null,
            sourceIsoOverride: null,
            disableSourceIsoOverride: true,
            toDomains: [],
            shouldTranslate: false,
            shouldCopySeo: false,
            fieldNamesAndValuesToCopyAndTranslate: [
              { title: map.title },
              { domain_id: null },
              { ski_slope_map_id: map.id },
            ],
            title: `Slope map: ${map.title}`,
          },
        );
      });
      return values;
    },
  },
  methods: {
    ...mapActions({
      fetchSlopeMaps: 'areas/fetchSlopeMaps',
      uploadImage: 'areas/uploadImage',
      removeImage: 'areas/removeImage',
      rearrangeImageList: 'areas/rearrangeImageList',
      imageUpdate: 'areas/imageUpdate',
    }),
    upload(event) {
      this.uploadImage({
        id: this.area.id,
        image: event,
        priority: this.slopeMaps.length + 1,
      }).then(() => {
        this.reset();
        this.saveSuccess = true;
        this.snackbar = true;
      }).catch(() => {
        this.fetchSlopeMaps(this.area.id);
        this.saveSuccess = false;
        this.error = true;
        this.snackbar = true;
      });
    },
    remove(event) {
      this.removeImage({
        id: this.area.id,
        image: event.image,
        images: event.images,
      }).then(() => {
        this.reset();
        this.deleteSuccess = true;
        this.snackbar = true;
      }).catch(() => {
        this.fetchSlopeMaps(this.area.id);
        this.deleteSuccess = false;
        this.error = true;
        this.snackbar = true;
      });
    },
    rearrange(event) {
      this.rearrangeImageList({
        id: this.area.id,
        images: event,
      }).then(() => {
        this.reset();
        this.rearrangeSuccess = true;
        this.snackbar = true;
      }).catch(() => {
        this.fetchSlopeMaps(this.area.id);
        this.rearrangeSuccess = false;
        this.error = true;
        this.snackbar = true;
      });
    },
    update(event) {
      this.imageUpdate({
        id: this.area.id,
        image: event.image,
        index: event.index,
        domains: event.domains,
      }).then(() => {
        this.reset();
        this.updateSuccess = true;
        this.snackbar = true;
      }).catch(() => {
        this.fetchSlopeMaps(this.area.id);
        this.updateSuccess = false;
        this.error = true;
        this.snackbar = true;
      });
    },
    reset() {
      this.error = false;
      this.saveSuccess = false;
      this.updateSuccess = false;
      this.deleteSuccess = false;
      this.rearrangeSuccess = false;
    },
    getSnackbarMessage() {
      let message = i18n.t('slopeMaps.snackbar.save.error');
      if (this.saveSuccess) {
        message = i18n.t('slopeMaps.snackbar.save.saveSuccess');
      } else if (this.rearrangeSuccess) {
        message = i18n.t('slopeMaps.snackbar.save.rearrangeSuccess');
      } else if (this.deleteSuccess) {
        message = i18n.t('slopeMaps.snackbar.save.deleteSuccess');
      } else if (this.updateSuccess) {
        message = i18n.t('slopeMaps.snackbar.save.updateSuccess');
      }
      return message;
    },
  },
  beforeRouteEnter(to, from, next) {
    if (!store.state.areas.area) {
      store.dispatch('areas/fetchArea', to.params.id).then(() => {
        store.dispatch('areas/fetchSlopeMaps', to.params.id);
        next();
      }).catch(() => {
        router.push({ name: 'SkiAreas' });
      });
    } else {
      store.dispatch('areas/fetchSlopeMaps', to.params.id);
      next();
    }
  },
};
</script>

<template>
  <v-flex class="ski-area-slope-maps">
    <v-flex py-4 px-4>
      <v-flex title mb-3>
        {{ $t('slopeMaps.upload.headings') }}
      </v-flex>
      <v-flex body-1 mb-3 v-html="$t('slopeMaps.upload.description')"/>
      <tw-image-upload :images="slopeMaps" @upload="upload"/>
      <v-btn
        v-if="slopeMaps.length"
        :loading="loading"
        color="primary success"
        @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
      >
        Copy/translate
      </v-btn>
      <tw-copy-and-translate-modal
        v-if="copyAndTranslateModalOpen"
        :elements-to-handle="getCopyValues"
        :domains="domains"
      />
    </v-flex>
    <v-divider/>
    <v-flex py-4 px-4>
      <v-flex title mb-3>
        {{ $t('slopeMaps.imageList.headings') }}
      </v-flex>
      <v-flex body-1 mb-3 v-html="$t('slopeMaps.imageList.description')"/>
      <ski-area-slope-maps-image-list
        :error="slopeMapError"
        :loading="loading"
        :images="slopeMaps"
        @update="update"
        @shuffle="rearrange"
        @move="rearrange"
        @remove="remove"
      />
    </v-flex>
    <v-divider/>
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      bottom
    >
      <v-icon
        v-if="deleteSuccess || saveSuccess || rearrangeSuccess || updateSuccess"
        class="mr-3 green--text"
      >check_circle</v-icon>
      <v-icon v-else class="mr-3 red--text">warning</v-icon>
      <v-flex>
        {{ getSnackbarMessage() }}
      </v-flex>
      <v-btn
        color="white"
        flat
        @click="snackbar = false"
      >
        {{ $t('slopeMaps.snackbar.close') }}
      </v-btn>
    </v-snackbar>
  </v-flex>
</template>



// WEBPACK FOOTER //
// src/pages/ski/areas/SkiAreaSlopeMaps.vue